import { Seo } from '../components/Seo'
import { PageHeader } from '../components/common/PageHeader'
import { breadcrumbSchema } from '../lib/seo'

type Doc = 'privacy' | 'terms' | 'disclaimer'

interface LegalSection {
  heading: string
  /** A plain string is a paragraph; a nested array is a bullet list, in order. */
  body: (string | string[])[]
}
interface LegalContent {
  path: string
  label: string
  title: string
  intro: string
  sections: LegalSection[]
}

const LAST_UPDATED = 'September 2026'

const CONTENT: Record<Doc, LegalContent> = {
  privacy: {
    path: '/privacy-policy',
    label: 'Privacy Policy',
    title: 'Privacy Policy',
    intro:
      'Greenbay Biotech International Pvt. Ltd. (“GreenbayBio”, “we”, “our”, or “us”) respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect information when you use the GreenbayBio website or the Green Bay Bio mobile application.',
    sections: [
      {
        heading: '1. Who This Policy Applies To',
        body: [
          'This Privacy Policy applies to users of our website and mobile application, including:',
          ['Farmers', 'Retailers and dealers', 'Distributors', 'GreenbayBio employees and authorised field staff', 'Visitors who submit enquiries through our website or App'],
          'The information available and collected may vary depending on your role and the services you use.',
        ],
      },
      {
        heading: '2. Information We Collect',
        body: [
          'We may collect the following information when required to provide our services.',
          'Personal and Contact Information — this may include:',
          ['Name', 'Mobile number', 'Email address, where provided', 'Address or location information, where required'],
          'Your registered mobile number may be used for account verification and login using a One-Time Password (OTP).',
          'Location Information — the App may request access to your device location for features that require location information.',
          'For authorised field staff, location may be used for purposes such as:',
          ['Attendance', 'Field visits', 'Route or travel records', 'Verification of business activities'],
          'For farmers, retailers, distributors, or other users, location may be used when necessary to:',
          ['Submit an enquiry', 'Identify nearby retailers or services', 'Record delivery or business locations', 'Provide location-based features'],
          'Location information is collected only when required for the relevant App feature and according to the permissions granted on your device.',
          'Photos and Camera — the App may request access to the camera or photos when you choose or are required to:',
          ['Capture attendance photographs', 'Upload visit photographs', 'Attach supporting documents', 'Submit expense or business-related evidence', 'Upload profile or other relevant images'],
          'Device and Technical Information — we may automatically collect limited technical information required to operate, secure, and improve the App, such as:',
          ['Device type', 'Operating system and App version', 'IP address', 'Notification information', 'App diagnostics and crash information'],
          'This information is used primarily for security, troubleshooting, reliability, and performance improvement.',
          'Business Information — depending on your role, the App may process business-related records such as:',
          ['Orders', 'Invoices', 'Payments', 'Account and ledger information', 'Stock transactions', 'Retailer or distributor information', 'Field visits', 'Attendance records', 'Expense records', 'Other business transactions'],
          'Enquiries — when you contact us or submit an enquiry through our website or App, we may collect information such as your:',
          ['Name', 'Mobile number', 'Email address', 'State, city, or location', 'Product of interest', 'Message or enquiry details'],
        ],
      },
      {
        heading: '3. How We Use Your Information',
        body: [
          'We use the information we collect to:',
          [
            'Create and manage user accounts',
            'Verify users and provide secure login',
            'Provide requested App and website features',
            'Process orders, payments, invoices, and business transactions',
            'Manage attendance and field activities',
            'Respond to product, retailer, distributor, or support enquiries',
            'Identify nearby retailers or relevant services',
            'Send important service and account notifications',
            'Prevent misuse and protect the security of our systems',
            'Diagnose technical problems and improve App performance',
            'Meet applicable legal, accounting, and regulatory requirements',
          ],
          'We do not sell personal information to third parties.',
        ],
      },
      {
        heading: '4. Notifications and Communication',
        body: [
          'We may use your registered mobile number, email address, or device notification service to send communications related to:',
          ['Account verification', 'Orders or transactions', 'Enquiries', 'Service updates', 'Business activities', 'Important account or security information'],
          'Where applicable, users may manage notification permissions through their device settings.',
        ],
      },
      {
        heading: '5. How We Share Information',
        body: [
          'We may share limited information with trusted service providers when required to operate our services.',
          'These may include providers used for:',
          ['OTP and messaging services', 'Push notifications', 'Cloud infrastructure', 'App diagnostics and crash reporting', 'Maps and location services'],
          'These providers process information only as necessary to provide their respective services.',
          'We may also disclose information when required by law, regulation, legal process, or a valid request from a government or regulatory authority.',
          'We do not sell or rent your personal information for advertising purposes.',
        ],
      },
      {
        heading: '6. Data Retention',
        body: [
          'We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy or as required by applicable law.',
          'Certain business, financial, accounting, invoice, payment, and tax records may need to be retained for longer periods to comply with legal and regulatory requirements.',
          'Location information, photographs, attendance records, and operational information may be retained for the period reasonably required for verification, business operations, dispute resolution, security, or audit purposes.',
          'When information is no longer required, we may delete or anonymise it according to our data-retention practices and applicable legal requirements.',
        ],
      },
      {
        heading: '7. Account Deletion',
        body: [
          'You may request deletion of your Green Bay Bio account.',
          'You can submit an account-deletion request:',
          ['Through the Delete Account option available in the App, where provided; or', 'Through our account-deletion page at greenbaybio.com/delete-account'],
          'We may verify your identity before processing a deletion request.',
          'After account deletion, we will remove or anonymise personal information that is no longer required.',
          'Certain information may continue to be retained where required for:',
          ['Taxation', 'Accounting', 'Regulatory compliance', 'Fraud prevention', 'Dispute resolution', 'Other legal obligations'],
          'Deleting your account will not require you to install the App if you submit the request through our website.',
        ],
      },
      {
        heading: '8. Your Permissions and Choices',
        body: [
          'Depending on your device and the features you use, you may control permissions such as:',
          ['Location', 'Camera', 'Photos or files', 'Notifications'],
          'You can review or change these permissions through your device settings.',
          'Some App features may not function correctly if a permission required for that feature is disabled.',
        ],
      },
      {
        heading: '9. Data Security',
        body: [
          'We use reasonable technical and organisational safeguards designed to protect personal and business information from unauthorised access, loss, misuse, alteration, or disclosure.',
          'Access to business information is restricted according to authorised user roles and permissions.',
          'However, no electronic system, internet transmission, or data-storage method can be guaranteed to be completely secure.',
        ],
      },
      {
        heading: '10. Children’s Privacy',
        body: [
          'Our business services are not designed to knowingly collect personal information from children without appropriate authorisation.',
          'If we become aware that personal information has been collected improperly from a child, we will take appropriate steps to address it.',
        ],
      },
      {
        heading: '11. Changes to This Privacy Policy',
        body: [
          'We may update this Privacy Policy from time to time to reflect changes to our services, legal requirements, or business practices.',
          'When we make material changes, we will update the Last updated date shown at the top of this page.',
        ],
      },
      {
        heading: '12. Contact Us',
        body: [
          'If you have questions about this Privacy Policy, your personal information, or an account-deletion request, you can contact us at:',
          'Greenbay Biotech International Pvt. Ltd., 2B, Grant Lane, 3rd Floor, Room No. 304, Kolkata – 700012, India',
          'Email: support@greenbaybio.com',
          'Phone: +91 33 4064 6022',
        ],
      },
    ],
  },
  terms: {
    path: '/terms',
    label: 'Terms of Use',
    title: 'Terms of Use',
    intro:
      'These Terms of Use (“Terms”) govern your use of the GreenbayBio website and the Green Bay Bio mobile application operated by Greenbay Biotech International Pvt. Ltd. (“GreenbayBio”, “we”, “our”, or “us”). By accessing or using our website or mobile application, you agree to these Terms.',
    sections: [
      {
        heading: '1. Use of Our Website',
        body: [
          'The GreenbayBio website provides information about our company, products, services, crop solutions, educational resources, and business opportunities.',
          'You may access and use the website for lawful personal or business purposes.',
          'You must not use the website in any way that could:',
          ['Violate applicable laws', 'Damage or interfere with the website', 'Attempt to gain unauthorised access to our systems', 'Misuse information made available through the website', 'Impersonate another person or organisation'],
        ],
      },
      {
        heading: '2. Green Bay Bio Mobile Application',
        body: [
          'The Green Bay Bio mobile application is intended for authorised users, which may include:',
          ['Farmers', 'Retailers or dealers', 'Distributors', 'GreenbayBio employees', 'Authorised field staff', 'Other approved business users'],
          'Available features may vary depending on your account type, role, location, and permissions.',
          'You may access only the features and information made available to your authorised account.',
        ],
      },
      {
        heading: '3. Your Account',
        body: [
          'You are responsible for:',
          ['Providing accurate information', 'Keeping your registered mobile number and account information up to date', 'Maintaining the security of your device and account', 'Protecting any OTP or verification code sent to you', 'Not allowing unauthorised persons to use your account'],
          'You must promptly inform us if you believe your account has been accessed or used without your permission.',
          'We may use verification or security measures to protect accounts and prevent unauthorised access.',
        ],
      },
      {
        heading: '4. Acceptable Use',
        body: [
          'When using our website or App, you must not:',
          [
            'Share verification codes or account access with unauthorised persons',
            'Access or attempt to access another user’s account',
            'Access information beyond the permissions assigned to your account',
            'Manipulate orders, attendance, visits, payments, pricing, or other business records',
            'Upload false, misleading, unlawful, or inappropriate information',
            'Attempt to bypass security controls',
            'Introduce viruses, malware, or harmful software',
            'Scrape, copy, extract, or collect data using automated means without permission',
            'Reverse engineer, modify, decompile, or attempt to reproduce the App or its systems except where expressly permitted by law',
            'Misuse confidential, financial, customer, retailer, distributor, farmer, or company information',
          ],
          'We may restrict or suspend access where misuse, fraud, security risks, or violations of these Terms are detected.',
        ],
      },
      {
        heading: '5. Business Transactions',
        body: [
          'Depending on your role, the App may allow you to create, view, approve, or manage business transactions such as:',
          ['Orders', 'Invoices', 'Payments', 'Stock records', 'Retailer or distributor transactions', 'Attendance', 'Field visits', 'Expense claims', 'Other operational records'],
          'You are responsible for ensuring that information entered or submitted by you is accurate and authorised.',
          'Transactions may be subject to separate commercial arrangements, invoices, purchase orders, credit policies, distributor agreements, retailer agreements, or other business terms.',
        ],
      },
      {
        heading: '6. Product Information',
        body: [
          'We make reasonable efforts to keep product information accurate and current.',
          'However, product:',
          ['Availability', 'Packaging', 'Specifications', 'Pricing', 'Recommendations', 'Registrations or approvals'],
          'may change from time to time.',
          'Information provided on the website or App does not replace the instructions provided on the actual product label, packaging, technical literature, or applicable regulatory documentation.',
          'Always follow the instructions, dosage, precautions, storage requirements, and usage directions provided with the product.',
        ],
      },
      {
        heading: '7. Agricultural Information and Results',
        body: [
          'Information relating to crops, soil, application methods, products, or agricultural practices is provided for general guidance.',
          'Agricultural results can vary depending on factors such as:',
          ['Crop type', 'Soil conditions', 'Climate', 'Water quality', 'Application method', 'Dosage', 'Pest or disease conditions', 'Local farming practices', 'Other environmental factors'],
          'We therefore do not guarantee any specific crop yield, agricultural result, or commercial outcome solely from the use of information provided through our website or App.',
        ],
      },
      {
        heading: '8. Intellectual Property',
        body: [
          'Unless otherwise stated, the GreenbayBio name, logos, branding, website content, App content, graphics, product information, designs, software, and other materials are owned by or licensed to Greenbay Biotech International Pvt. Ltd.',
          'You may not reproduce, modify, distribute, commercially exploit, or use our intellectual property without prior permission, except where permitted by applicable law.',
        ],
      },
      {
        heading: '9. Third-Party Services',
        body: [
          'Some features may depend on third-party services such as:',
          ['Messaging services', 'Maps', 'Push notifications', 'Payment or banking services', 'Cloud infrastructure', 'Other external service providers'],
          'Your use of certain third-party services may also be subject to the respective provider’s terms and policies.',
          'We are not responsible for services provided independently by third parties outside our control.',
        ],
      },
      {
        heading: '10. Availability of Services',
        body: [
          'We aim to keep our website and App available and reliable, but uninterrupted availability cannot be guaranteed.',
          'Services may occasionally be unavailable because of:',
          ['Maintenance', 'Technical problems', 'Internet or mobile-network failures', 'Third-party service interruptions', 'Security incidents', 'Updates or system changes', 'Circumstances beyond our reasonable control'],
          'We may update, modify, add, or discontinue features when reasonably necessary.',
        ],
      },
      {
        heading: '11. Account Suspension or Termination',
        body: [
          'We may temporarily restrict, suspend, or deactivate an account where reasonably necessary because of:',
          ['Violation of these Terms', 'Suspected fraud or misuse', 'Unauthorised account access', 'Security concerns', 'Legal or regulatory requirements', 'Termination of the user’s business or employment relationship with GreenbayBio', 'Other legitimate business reasons'],
          'Where appropriate, access may be restored after verification or resolution of the relevant issue.',
        ],
      },
      {
        heading: '12. Account Deletion',
        body: [
          'You may request deletion of your account through:',
          ['The Delete Account option in the App, where available; or', 'greenbaybio.com/delete-account'],
          'We may verify your identity before processing the request.',
          'When an account is deleted, personal information that is no longer required will be deleted or anonymised.',
          'Certain information may continue to be retained where required for:',
          ['Tax or accounting purposes', 'Legal or regulatory compliance', 'Fraud prevention', 'Audit requirements', 'Dispute resolution', 'Other lawful business requirements'],
          'For more information about how we handle personal information, please refer to our Privacy Policy.',
        ],
      },
      {
        heading: '13. Disclaimer',
        body: [
          'Our website, App, and related information are provided on an “as available” basis.',
          'While we take reasonable care in providing our services, we do not guarantee that:',
          ['Every feature will always be available', 'All information will always be free from errors', 'The website or App will operate without interruption', 'Use of a particular product will produce a specific agricultural or commercial result'],
          'Nothing in these Terms excludes any rights or liabilities that cannot legally be excluded under applicable law.',
        ],
      },
      {
        heading: '14. Limitation of Liability',
        body: [
          'To the extent permitted by applicable law, GreenbayBio will not be responsible for indirect, incidental, or consequential losses resulting from the use or inability to use the website or App.',
          'This limitation does not apply where liability cannot legally be limited or excluded.',
        ],
      },
      {
        heading: '15. Changes to These Terms',
        body: [
          'We may update these Terms from time to time to reflect changes in:',
          ['Our services', 'Business practices', 'Technology', 'Security requirements', 'Applicable laws or regulations'],
          'The latest version will be published on our website with the updated effective date.',
          'Continued use of our services after an updated version becomes effective constitutes acceptance of the updated Terms, where permitted by law.',
        ],
      },
      {
        heading: '16. Governing Law and Jurisdiction',
        body: [
          'These Terms are governed by the laws of India.',
          'Subject to applicable law, disputes relating to these Terms or our services will be subject to the jurisdiction of the competent courts in Kolkata, West Bengal, India.',
        ],
      },
      {
        heading: '17. Contact Us',
        body: [
          'For questions about these Terms or our services, contact:',
          'Greenbay Biotech International Pvt. Ltd., 2B, Grant Lane, 3rd Floor, Room No. 304, Kolkata – 700012, India',
          'Email: support@greenbaybio.com',
          'Phone: +91 33 4064 6022',
        ],
      },
    ],
  },
  disclaimer: {
    path: '/disclaimer',
    label: 'Disclaimer',
    title: 'Disclaimer',
    intro: 'Please read this disclaimer before relying on any information on this website.',
    sections: [
      {
        heading: 'General information only',
        body: [
          'The agronomic guidance, crop recommendations and dosage summaries on this website are general information to help you get started. They are not a substitute for advice tailored to your specific field, soil and conditions.',
          'Always follow the exact dosage, timing and precautions on the product label, and consult a Greenbay agronomic advisor or a qualified agronomist for your situation.',
        ],
      },
      {
        heading: 'No guarantee of results',
        body: [
          'We make no guarantee of any particular yield, income or outcome. Product performance depends on soil condition, climate, crop management and other factors beyond our control.',
        ],
      },
      {
        heading: 'Approvals & certifications',
        body: [
          'Approvals and certifications (such as NPOP, FCO, EU/NOP and USDA BioPreferred®) apply to specific products as stated on each product page, and may vary by product and region.',
        ],
      },
      {
        heading: 'Imagery',
        body: [
          'Some gallery imagery on this website is represented by branded placeholders and will be replaced with actual photography.',
        ],
      },
    ],
  },
}

export default function LegalPage({ doc }: { doc: Doc }) {
  const c = CONTENT[doc]
  return (
    <>
      <Seo
        title={`${c.label} | GreenbayBio`}
        description={c.intro}
        path={c.path}
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: c.label, path: c.path }])]}
      />
      <PageHeader eyebrow="Legal" title={c.title} subtitle={c.intro} />

      <div className="container-x max-w-prose py-14">
        <p className="mb-8 text-sm text-ink-500">Last updated: {LAST_UPDATED}</p>
        <div className="space-y-10">
          {c.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-display text-h3 font-semibold text-ink">{s.heading}</h2>
              <div className="mt-3 space-y-3 leading-relaxed text-ink-600">
                {s.body.map((p, i) =>
                  Array.isArray(p) ? (
                    <ul key={i} className="list-disc space-y-1 pl-5">
                      {p.map((li, j) => (
                        <li key={j}>{li}</li>
                      ))}
                    </ul>
                  ) : (
                    <p key={i}>{p}</p>
                  ),
                )}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}
